/* Admin Center — Expense Types tab (extracted from AdminPage.tsx) */

import { useState, useEffect } from 'react';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import { GripVertical, Trash2 } from 'lucide-react';
import EmptyState from '../../components/EmptyState';
import { AdminService } from '../../services/adminService';
import { useTableSync } from '../../hooks/useTableSync';
import type { AdminPageProps } from './types';
import {
  inputStyle,
  btnPrimary,
  btnDanger,
  DRAG_HANDLE_STYLE,
  useDragReorder,
  RowSkeleton,
} from './shared';

/* ─── Expense Types Tab (with Rate column) ─── */
function ExpenseTypesTab({ toast }: { toast: AdminPageProps['toast'] }) {
  const [items, setItems] = useState<any[]>([]);
  const [newName, setNewName] = useState('');
  const [newRate, setNewRate] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingRate, setEditingRate] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);
  useTableSync(['expense_types'], () => load());
  async function load() {
    try {
      const rows = await AdminService.expenseTypes.list();
      rows.sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      setItems(rows);
    } finally {
      setLoading(false);
    }
  }

  const drag = useDragReorder(items, async (reordered) => {
    setItems(reordered);
    try {
      await Promise.all(
        reordered.map((it: any, i) => {
          if ((items.find((x) => x.id === it.id)?.sortOrder ?? -1) !== i) {
            return AdminService.expenseTypes.save({ ...it, sortOrder: i });
          }
          return Promise.resolve();
        })
      );
    } catch (err: any) {
      toast('Reorder failed: ' + err.message, true);
      await load();
    }
  });

  async function add() {
    if (!newName.trim()) return;
    try {
      await AdminService.expenseTypes.save({
        id: '',
        name: newName.trim(),
        rate: newRate ? parseFloat(newRate) : undefined,
        sortOrder: items.length,
        createdAt: '',
        updatedAt: '',
      } as any);
      setNewName('');
      setNewRate('');
      await load();
      toast('Expense type added');
    } catch (err: any) {
      toast('Save failed: ' + err.message, true);
    }
  }

  async function update(id: string) {
    if (!editingName.trim()) return;
    const item = items.find((i) => i.id === id);
    if (item) {
      try {
        await AdminService.expenseTypes.save({
          ...item,
          name: editingName.trim(),
          rate: editingRate ? parseFloat(editingRate) : undefined,
        });
        setEditingId(null);
        await load();
        toast('Expense type updated');
      } catch (err: any) {
        toast('Update failed: ' + err.message, true);
      }
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this expense type?')) return;
    try {
      await AdminService.expenseTypes.delete(id);
      await load();
      toast('Expense type deleted');
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  return (
    <div>
      <div
        style={{
          fontSize: 'var(--font-size-base)',
          color: 'var(--tm)',
          marginBottom: 'var(--space-4)',
        }}
      >
        Define expense types for expense reports. Set a rate for per-unit expenses (e.g., mileage
        $/mile). Leave rate blank for flat-amount expenses.
      </div>

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-4)',
          alignItems: 'flex-end',
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--tm)',
              marginBottom: rem(3),
            }}
          >
            Type Name
          </div>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder="e.g. Mileage..."
            style={inputStyle}
          />
        </div>
        <div style={{ width: rem(120) }}>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--tm)',
              marginBottom: rem(3),
            }}
          >
            Rate ($/unit)
          </div>
          <input
            value={newRate}
            onChange={(e) => setNewRate(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder="optional"
            type="number"
            step="0.01"
            min="0"
            style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace" }}
          />
        </div>
        <button onClick={add} style={{ ...btnPrimary, marginBottom: 0 }}>
          + Add
        </button>
      </div>

      <div
        style={{
          background: 'var(--sf)',
          borderRadius: rem(8),
          border: '1px solid var(--bd)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <RowSkeleton label="Loading expense types" />
        ) : items.length === 0 ? (
          <EmptyState
            title="No expense types defined"
            subtitle="Defaults will be seeded automatically."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                display: 'flex',
                gap: 'var(--space-2)',
                padding: `${rem(6)} var(--space-3)`,
                borderBottom: '1px solid var(--bd)',
                background: 'var(--sa)',
              }}
            >
              <span style={{ width: rem(24) }}></span>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 700,
                  color: 'var(--tm)',
                  textTransform: 'uppercase',
                  flex: 1,
                }}
              >
                Type
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 700,
                  color: 'var(--tm)',
                  textTransform: 'uppercase',
                  width: rem(100),
                  textAlign: 'right',
                }}
              >
                Rate
              </span>
              <span style={{ width: rem(100) }}></span>
            </div>
            {items.map((item, i) => {
              const isDragging = drag.dragIdx === i;
              const isDropTarget =
                drag.overIdx === i && drag.dragIdx !== null && drag.dragIdx !== i;
              return (
                <div
                  key={item.id}
                  {...drag.rowProps(i)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-3)',
                    borderBottom: i < items.length - 1 ? '1px solid var(--bd)' : 'none',
                    background: isDragging
                      ? 'rgba(var(--acr),0.08)'
                      : isDropTarget
                        ? 'rgba(var(--acr),0.04)'
                        : undefined,
                    opacity: isDragging ? 0.5 : 1,
                    transition: 'background .1s',
                  }}
                >
                  <span
                    {...drag.handleProps()}
                    style={{ ...DRAG_HANDLE_STYLE, ...drag.handleProps().style }}
                    title="Drag to reorder"
                    aria-label="Drag to reorder"
                  >
                    <Icon icon={GripVertical} em={14 / 16} />
                  </span>
                  {editingId === item.id ? (
                    <>
                      <input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && update(item.id)}
                        style={{ ...inputStyle, flex: 1 }}
                        autoFocus
                      />
                      <input
                        value={editingRate}
                        onChange={(e) => setEditingRate(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && update(item.id)}
                        placeholder="N/A"
                        type="number"
                        step="0.01"
                        min="0"
                        style={{
                          ...inputStyle,
                          width: rem(100),
                          fontFamily: "'JetBrains Mono', monospace",
                        }}
                      />
                      <button
                        onClick={() => update(item.id)}
                        style={{ ...btnPrimary, padding: `${rem(5)} ${rem(10)}` }}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        style={{ ...btnDanger, color: 'var(--tm)' }}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ flex: 1, fontSize: 'var(--font-size-base)' }}>
                        {item.name}
                      </span>
                      <span
                        style={{
                          width: rem(100),
                          textAlign: 'right',
                          fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                          fontFamily: "'JetBrains Mono', monospace",
                          color: item.rate != null ? 'var(--tx)' : 'var(--tm)',
                        }}
                      >
                        {item.rate != null ? `$${item.rate.toFixed(2)}/unit` : 'N/A'}
                      </span>
                      <button
                        onClick={() => {
                          setEditingId(item.id);
                          setEditingName(item.name);
                          setEditingRate(item.rate != null ? String(item.rate) : '');
                        }}
                        style={{ ...btnDanger, color: 'var(--tm)' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => remove(item.id)}
                        style={btnDanger}
                        title="Delete expense type"
                        aria-label={`Delete expense type ${item.name}`}
                      >
                        <Icon icon={Trash2} em={14 / 12} />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export { ExpenseTypesTab };
